export default function PrivacyScreen() {
  return (
    <div className="p-6 font-sans text-gray-800">
      <h1 className="text-3xl font-bold mb-4">Privacy Policy</h1>
      <p className="text-sm text-gray-600 mb-6">
        Effective Date: March 12, 2026
      </p>

      <p className="mb-4">
        This Privacy Policy explains how we collect, use, and protect your
        information when you use our app ("App"). By using this App, you agree
        to the terms outlined in this Privacy Policy.
      </p>

      <h2 className="text-2xl font-semibold mb-3">Information We Collect</h2>
      <p className="mb-4">
        When you use our App, we may collect the following information:
      </p>
      <ul className="list-disc list-inside mb-4">
        <li>First and last name</li>
        <li>Email address</li>
        <li>Phone number</li>
        <li>Login credentials (such as password, in encrypted form)</li>
        <li>
          Basic profile information (such as date of birth, gender and language
          preferences)
        </li>
        <li>
          Usage information about how you interact with the App (such as access
          dates and times, features used and technical logs)
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-3">
        Health and Fitness Data ("Health Data")
      </h2>
      <p className="mb-4">
        Because our App is focused on training and fitness, we may collect and
        process Health Data that you choose to provide to us, including:
      </p>
      <ul className="list-disc list-inside mb-4">
        <li>Physical measurements (such as height, weight and body metrics)</li>
        <li>
          Training, workout and exercise history (such as exercises performed,
          loads, repetitions, duration and training plans)
        </li>
        <li>
          Fitness goals and preferences (such as weight goals, performance or
          well-being objectives)
        </li>
        <li>
          Information about injuries, restrictions or physical conditions that
          you choose to register to adapt your training
        </li>
        <li>
          Other health or fitness information that you voluntarily enter in the
          App to monitor your progress
        </li>
      </ul>
      <p className="mb-4">
        If you choose to connect the App with third‑party health platforms or
        services (such as device or sensor integrations offered by your
        smartphone or operating system), we will only access the specific
        Health Data that you explicitly authorize through those services, and we
        will process such data only for the purposes described in this Privacy
        Policy.
      </p>

      <h2 className="text-2xl font-semibold mb-3">
        How We Use Your Information
      </h2>
      <p className="mb-4">
        We use the information we collect for the following purposes:
      </p>
      <ul className="list-disc list-inside mb-4">
        <li>To create and manage your account.</li>
        <li>To provide customer support.</li>
        <li>To improve our App and user experience.</li>
      </ul>

      <h2 className="text-2xl font-semibold mb-3">
        How We Use Health Data
      </h2>
      <p className="mb-4">
        We use your Health Data exclusively to operate and improve the fitness
        and training features of the App, including:
      </p>
      <ul className="list-disc list-inside mb-4">
        <li>To create, adjust and manage your training plans.</li>
        <li>To track your physical progress over time.</li>
        <li>
          To generate statistics, charts and insights about your performance and
          evolution.
        </li>
        <li>
          To allow professionals or staff connected to your account (when
          applicable) to view your evolution and adjust your training.
        </li>
        <li>
          To personalize recommendations, alerts and content related to your
          training and goals.
        </li>
      </ul>
      <p className="mb-4">
        We do not use your Health Data to show you third‑party advertising, we
        do not sell your Health Data, and we do not use your Health Data for
        purposes that are unrelated to health, fitness or training.
      </p>

      <h2 className="text-2xl font-semibold mb-3">Sharing Your Information</h2>
      <p className="mb-4">
        We do not share your personal information with third parties, except as
        required by law or to provide necessary services (e.g., hosting
        providers).
      </p>
      <p className="mb-4">
        Health Data may be shared only in the following situations:
      </p>
      <ul className="list-disc list-inside mb-4">
        <li>
          With service providers that process data on our behalf (such as
          hosting, analytics or infrastructure providers), under contracts that
          require confidentiality and data protection.
        </li>
        <li>
          With professionals or entities directly linked to your use of the App
          (for example, your gym or trainer), when this sharing is necessary to
          deliver the services you requested.
        </li>
        <li>
          When required by law, regulation, legal process or governmental
          request.
        </li>
        <li>
          In aggregated or anonymized form, which does not identify you
          individually.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-3">Data Retention</h2>
      <p className="mb-4">
        We retain your information as long as necessary to provide our services
        or as required by law. You may request the deletion of your data by
        contacting us.
      </p>
      <p className="mb-4">
        Health Data is retained while your account remains active or while it is
        necessary to provide you with training, monitoring and related services.
        If you request deletion of your account or Health Data, we will delete
        or irreversibly anonymize this information, except when we are required
        to retain certain data for legal, regulatory or legitimate business
        reasons.
      </p>

      <h2 className="text-2xl font-semibold mb-3">Your Rights</h2>
      <p className="mb-4">
        You have the right to access, update, or delete your personal
        information. To exercise these rights, please contact us using the
        information provided below.
      </p>
      <p className="mb-4">
        You may also request:
      </p>
      <ul className="list-disc list-inside mb-4">
        <li>
          Information about which types of Health Data we process in connection
          with your account.
        </li>
        <li>
          Correction or update of Health Data that is inaccurate or incomplete.
        </li>
        <li>
          Deletion of Health Data, subject to applicable legal obligations.
        </li>
        <li>
          Revocation of consents you have given, including for connections with
          third‑party health services (where applicable).
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-3">Contact Us</h2>
      <p className="mb-4">
        If you have any questions or concerns about this Privacy Policy, please
        contact us at:
      </p>
      <p className="font-medium">
        Email:{" "}
        <a
          href="mailto:support@gymlabapp.com.br"
          className="text-blue-600 underline"
        >
          support@gymlabapp.com.br
        </a>
      </p>
    </div>
  );
}
